import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router'; // ✅ NECESSARIO per routerLink
import { SeoService } from '../../seo/seo.service';

// ✅ Se Navbar/Footer sono già nel layout globale (app component), NON importarli qui
// import { NavbarComponent } from '../../components/navbar/navbar';
// import { FooterComponent } from '../../components/footer/footer';

type BlogPost = {
  title: string;
  slug: string;          // ✅ per link SEO: /blog/{slug}
  img: string;
  date: string;
  excerpt: string;
  category: string;
  readingTime: string;
  published: boolean;    // ✅ per gestire "in arrivo"
};

@Component({
  selector: 'app-blog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule, // ✅ abilita [routerLink]
    // NavbarComponent,
    // FooterComponent,
  ],
  templateUrl: './blog.html',
  styleUrls: ['./blog.scss'],
})
export class BlogComponent implements OnInit {
  query = '';
  category = 'Tutti';

  posts: BlogPost[] = [
    {
      title: 'Quanto costano le pulizie di un appartamento a Pescara?',
      slug: 'quanto-costano-pulizie-appartamento-pescara',
      img: 'assets/quanto-costa-la-pulizia.webp',
      date: '25 Marzo 2025',
      excerpt:
        'Cosa incide sul costo: metratura, frequenza, condizioni iniziali e tipologia di servizio.',
      category: 'Costi',
      readingTime: '3 min',
      published: true,
    },
    {
      title: 'Differenza tra pulizie ordinarie e straordinarie',
      slug: 'differenza-pulizie-ordinarie-straordinarie',
      img: 'assets/differenza.webp',
      date: '15 Marzo 2025',
      excerpt:
        'Le ordinarie mantengono l’igiene, le straordinarie sono interventi profondi occasionali.',
      category: 'Guide',
      readingTime: '4 min',
      published: true,
    },
    {
      title: 'Sanificazioni vs Igienizzazioni: cosa cambia davvero?',
      slug: 'sanificazioni-vs-igienizzazioni-cosa-cambia',
      img: 'assets/sanifficazioni.webp',
      date: '3 Marzo 2025',
      excerpt:
        'Sanificazione e igienizzazione non sono la stessa cosa: ecco come distinguerle.',
      category: 'Sanificazione',
      readingTime: '4 min',
      published: true,
    },

    // 🔒 esempi "in arrivo"
    {
      title: 'Cosa valutare quando scegli un’impresa di pulizie',
      slug: 'cosa-valutare-impresa-di-pulizie',
      img: 'assets/valutare.webp',
      date: '20 Febbraio 2025',
      excerpt:
        'Referenze, servizi, trasparenza nei prezzi, materiali e certificazioni.',
      category: 'Consigli',
      readingTime: '5 min',
      published: true,
    },
    {
      title: '5 consigli per mantenere la casa sempre pulita',
      slug: '5-consigli-casa-sempre-pulita',
      img: 'assets/consigli.webp',
      date: '26 Marzo 2025',
      excerpt:
        'Piccole abitudini quotidiane per mantenere la casa sempre in ordine.',
      category: 'Casa',
      readingTime: '3 min',
      published: true,
    },
    {
      title: 'Come pulire casa con prodotti naturali',
      slug: 'pulire-casa-prodotti-naturali',
      img: 'assets/eco.webp',
      date: '24 Marzo 2025',
      excerpt:
        'Ingredienti naturali utili per pulire senza chimici aggressivi.',
      category: 'Eco',
      readingTime: '4 min',
      published: true,
    },
  ];

  filteredPosts: BlogPost[] = [];
  categories: string[] = [];

  constructor(private seo: SeoService) {}

  ngOnInit(): void {
    this.categories = Array.from(new Set(this.posts.map(p => p.category))).sort();
    this.filteredPosts = [...this.posts];

    this.seo.updateTags({
      title: 'Blog pulizie e sanificazioni a Pescara | SAMI Pulizie',
      description:
        'Articoli e consigli sul mondo delle pulizie professionali: sanificazioni, costi, trucchi e guide. Blog ufficiale SAMI Pulizie Pescara.',
      keywords:
        'blog pulizie Pescara, consigli pulizie, sanificazione, igienizzazione, impresa di pulizie Pescara',
      canonicalUrl: '/blog',

      ogTitle: 'Blog SAMI Pulizie',
      ogDescription:
        'Consigli e guide per pulizie professionali e sanificazioni a Pescara.',
      ogImage: '/assets/logo.png',
      ogType: 'website',

      twitterCard: 'summary_large_image',
      twitterTitle: 'Blog SAMI Pulizie',
      twitterDescription:
        'Consigli e guide per pulizie professionali e sanificazioni a Pescara.',
      twitterImage: '/assets/logo.png',

      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'SAMI Pulizie - Blog',
        url: 'https://samipulizie.it/blog',
        description:
          'Consigli e guide per pulizie professionali, domestiche e sanificazioni a Pescara.',
        publisher: {
          '@type': 'Organization',
          name: 'SAMI Pulizie',
          logo: {
            '@type': 'ImageObject',
            url: 'https://samipulizie.it/assets/logo.png',
          },
        },
        blogPost: this.posts
          .filter(p => p.published)
          .map(p => ({
            '@type': 'BlogPosting',
            headline: p.title,
            description: p.excerpt,
            url: `https://samipulizie.it/blog/${p.slug}`,
            datePublished: this.normalizeDateForSchema(p.date),
          })),
      },
    });
  }

  applyFilters(): void {
    const q = this.query.trim().toLowerCase();

    this.filteredPosts = this.posts.filter(p => {
      const matchQuery =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.slug.toLowerCase().includes(q);

      const matchCategory =
        this.category === 'Tutti' || p.category === this.category;

      return matchQuery && matchCategory;
    });
  }

  private normalizeDateForSchema(dateIt: string): string {
    try {
      const parts = dateIt.trim().split(' ');
      if (parts.length < 3) return '';

      const day = parts[0].padStart(2, '0');
      const monthName = parts[1].toLowerCase();
      const year = parts[2];

      const months: Record<string, string> = {
        gennaio: '01',
        febbraio: '02',
        marzo: '03',
        aprile: '04',
        maggio: '05',
        giugno: '06',
        luglio: '07',
        agosto: '08',
        settembre: '09',
        ottobre: '10',
        novembre: '11',
        dicembre: '12',
      };

      const month = months[monthName];
      if (!month) return '';

      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  }
}
